import { useNavigate } from "@tanstack/react-router";
import {
  InfoCircle,
  Mail02,
  MessageTextSquare02,
  Ruler,
  SearchMd,
  Settings04,
  ShoppingBag01,
  Trash01,
} from "@untitledui/icons";
import { useCallback, useMemo, useState } from "react";
import { useCreateTargetCollaboration } from "@/api/collaborations";
import { useProducts } from "@/api/products";
import { useSampleRulesCount } from "@/api/sample-rules";
import { Progress } from "@/components/base/application/progress-steps/progress-steps";
import type { Step } from "@/components/base/application/progress-steps/progress-types";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Toggle } from "@/components/base/toggle/toggle";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { MessageComposer } from "@/components/message-composer/message-composer";
import { SampleRulesDrawer } from "@/components/sample-requests/sample-rules-drawer";
import { Dialog, DialogTitle, ScrollableDialogContent } from "@/components/ui/dialog";
import { useCurrency } from "@/hooks/use-currency";
import { useClearableDebounce } from "@/hooks/use-debounce";
import { MAX_INVITATION_MESSAGE_LENGTH } from "@/pages/collab-groups/modals/create-collab-group-modal";
import { useCollabTriggerTypes } from "@/pages/sequences/collab-trigger-types";
import { useMerchant } from "@/providers/MerchantProvider";
import { error as toastError } from "@/providers/ToastProvider";
import { cx } from "@/utils/cx";
import { type AutomationsFormState, AutomationsStep, INITIAL_AUTOMATIONS } from "./collaboration-automations-step";

const STEP_LABELS = ["Details", "Product", "Samples", "Invitation", "Automations"] as const;
const LAST_STEP_INDEX = (STEP_LABELS.length - 1) as StepIndex;
type StepIndex = 0 | 1 | 2 | 3 | 4;

// TikTok caps collaboration names at 30 chars; we reserve 3 chars so " 99"-style
// overflow sibling suffixes stay within budget.
const MAX_COLLABORATION_NAME_LENGTH = 27;

type ProductEntry = {
  id: string;
  name: string;
  thumbnail: string | null;
  price: string;
  commissionRate: string;
  shopAdsEnabled: boolean;
  shopAdsCommission: string;
};

type ApprovalRule = "auto-approve" | "manually-review" | null;

type FormState = {
  name: string;
  activeUntil: string;
  products: ProductEntry[];
  offerFreeSamples: boolean;
  approvalRule: ApprovalRule;
  invitationMessage: string;
  sellerEmail: string;
  automations: AutomationsFormState;
};

const INITIAL_FORM: FormState = {
  name: "",
  activeUntil: "",
  products: [],
  offerFreeSamples: true,
  approvalRule: "auto-approve",
  invitationMessage: "",
  sellerEmail: "",
  automations: INITIAL_AUTOMATIONS,
};

function formatPrice(prices: number[], currency: string): string {
  if (!prices.length) return "";
  const min = Math.min(...prices);
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(min);
}

function buildSteps(currentStep: StepIndex, labels: readonly string[]): Step[] {
  return labels.map((label, i) => ({
    title: label,
    description: "",
    status: i < currentStep ? "complete" : i === currentStep ? "current" : "incomplete",
  }));
}

// ============================================================================
// Step 1: Details
// ============================================================================

function DetailsStep({
  form,
  onChange,
  errors,
}: {
  form: FormState;
  onChange: (patch: Partial<FormState>) => void;
  errors: Record<string, string>;
}) {
  return (
    <div className="flex gap-8">
      <div className="w-48 shrink-0 flex gap-2">
        <Settings04 className="size-5 shrink-0 translate-y-[2px] text-tertiary" />
        <div>
          <p className="text-sm font-semibold text-primary">Collaboration details</p>
          <p className="text-sm text-tertiary">Name and set up this collaboration</p>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-5">
        <div>
          <Input label="Collaboration Type" value="Target" isDisabled />
        </div>
        <div>
          <Input
            label="Collaboration Name"
            isRequired
            value={form.name}
            onChange={(v) => onChange({ name: v })}
            isInvalid={!!errors.name}
            hint={errors.name ?? `Max ${MAX_COLLABORATION_NAME_LENGTH} characters`}
            placeholder="Enter collaboration name"
            maxLength={MAX_COLLABORATION_NAME_LENGTH}
          />
        </div>
        <div>
          <Input
            label="Active Until"
            isRequired
            type="date"
            value={form.activeUntil}
            onChange={(v) => onChange({ activeUntil: v })}
            isInvalid={!!errors.activeUntil}
            hint={errors.activeUntil}
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step 2: Products
// ============================================================================

function ProductsStep({
  form,
  onChange,
  errors,
  currency,
}: {
  form: FormState;
  onChange: (patch: Partial<FormState>) => void;
  errors: Record<string, string>;
  currency: string;
}) {
  const [search, setSearch] = useState("");
  const { value: debouncedSearch } = useClearableDebounce(search);
  const { data: allProducts } = useProducts(debouncedSearch || undefined);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const selectedIds = useMemo(() => new Set(form.products.map((p) => p.id)), [form.products]);

  const filteredProducts = useMemo(() => {
    if (!allProducts) return [];
    return allProducts.filter((p) => !selectedIds.has(p.productId));
  }, [allProducts, selectedIds]);

  const addProduct = useCallback(
    (product: { productId: string; productName: string; thumbnail: string | null; prices: number[] }) => {
      const entry: ProductEntry = {
        id: product.productId,
        name: product.productName,
        thumbnail: product.thumbnail,
        price: formatPrice(product.prices, currency),
        commissionRate: "",
        shopAdsEnabled: false,
        shopAdsCommission: "",
      };
      onChange({ products: [...form.products, entry] });
      setSearch("");
      setIsDropdownOpen(false);
    },
    [form.products, onChange, currency],
  );

  const removeProduct = useCallback(
    (id: string) => {
      onChange({ products: form.products.filter((p) => p.id !== id) });
    },
    [form.products, onChange],
  );

  const updateProduct = useCallback(
    (id: string, patch: Partial<ProductEntry>) => {
      onChange({
        products: form.products.map((p) => (p.id === id ? { ...p, ...patch } : p)),
      });
    },
    [form.products, onChange],
  );

  return (
    <div className="flex gap-8">
      <div className="w-48 shrink-0 flex gap-2">
        <ShoppingBag01 className="size-5 shrink-0 translate-y-[2px] text-tertiary" />
        <div>
          <p className="text-sm font-semibold text-primary">Products</p>
          <p className="text-sm text-tertiary">Add products and set commission rates</p>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-4 min-w-0">
        {form.products.map((product) => (
          <div
            key={product.id}
            className={cx(
              "rounded-lg border bg-secondary",
              errors[`product_${product.id}`] ? "border-error" : "border-primary",
            )}
          >
            <div className="flex items-center gap-3 p-3 min-w-0">
              {product.thumbnail ? (
                <img src={product.thumbnail} alt="" className="size-10 shrink-0 rounded-md object-cover" />
              ) : (
                <div className="size-10 shrink-0 rounded-md bg-tertiary" />
              )}
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="truncate text-sm font-medium text-primary">{product.name}</p>
                <p className="text-sm text-tertiary">{product.price}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="relative">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={product.commissionRate}
                    onChange={(e) => updateProduct(product.id, { commissionRate: e.target.value })}
                    placeholder="0"
                    className={cx(
                      "w-16 rounded-lg border bg-primary px-2 py-1.5 pr-6 text-sm text-right outline-hidden ring-1 ring-inset",
                      errors[`product_${product.id}`] ? "ring-error" : "ring-primary focus:ring-2 focus:ring-brand",
                    )}
                  />
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-tertiary">
                    %
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeProduct(product.id)}
                  className="p-1 text-tertiary hover:text-primary"
                >
                  <Trash01 className="size-4" />
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-primary px-3 py-2">
              <div className="flex items-center gap-1">
                <span className="text-sm text-tertiary">Shop Ads Commission</span>
                <Tooltip
                  title="Override commission rate for ad-attributed orders only."
                  description="If left unset, orders from creator video ads default to whichever is higher: your Open Collaboration Shop Ads rate or this invitation's standard rate."
                  arrow
                  placement="bottom"
                >
                  <TooltipTrigger>
                    <InfoCircle className="size-3.5 text-fg-quaternary" />
                  </TooltipTrigger>
                </Tooltip>
              </div>
              <div className="flex items-center gap-2">
                <Toggle
                  size="sm"
                  isSelected={product.shopAdsEnabled}
                  onChange={(val) => updateProduct(product.id, { shopAdsEnabled: val })}
                />
                {product.shopAdsEnabled && (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={product.shopAdsCommission}
                      onChange={(e) => updateProduct(product.id, { shopAdsCommission: e.target.value })}
                      className="w-14 rounded-lg border bg-primary px-2 py-1 text-sm text-right outline-hidden ring-1 ring-primary ring-inset focus:ring-2 focus:ring-brand"
                    />
                    <span className="text-sm text-tertiary">%</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {errors.products && <p className="text-sm text-error">{errors.products}</p>}

        <div className="relative">
          <Input
            icon={SearchMd}
            placeholder="Search for a product to add..."
            value={search}
            onChange={(v) => {
              setSearch(v);
              setIsDropdownOpen(true);
            }}
            onFocus={() => setIsDropdownOpen(true)}
          />
          {isDropdownOpen && search && filteredProducts.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-primary bg-primary shadow-lg">
              {filteredProducts.map((product) => (
                <button
                  key={product.productId}
                  type="button"
                  onClick={() => addProduct(product)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-primary_hover"
                >
                  {product.thumbnail ? (
                    <img src={product.thumbnail} alt="" className="size-8 rounded object-cover" />
                  ) : (
                    <div className="size-8 rounded bg-secondary" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-primary">{product.productName}</p>
                    <p className="text-xs text-tertiary">{formatPrice(product.prices, currency)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step 3: Samples
// ============================================================================

function SamplesStep({ form, onChange }: { form: FormState; onChange: (patch: Partial<FormState>) => void }) {
  const { data: rulesCountData } = useSampleRulesCount();
  const rulesCount = rulesCountData?.total ?? 0;
  const [rulesDrawerOpen, setRulesDrawerOpen] = useState(false);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-base font-semibold text-primary">Free Samples</p>
        <p className="text-sm text-quaternary mt-1">
          Control whether creators can request samples, and set the rules for auto-approval
        </p>
      </div>

      <div className="flex items-center justify-between rounded-lg border border-primary bg-secondary px-4 py-3">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-primary">Offer Free Samples</span>
          <Tooltip title="Allow creators to request free product samples" arrow placement="bottom">
            <TooltipTrigger>
              <InfoCircle className="size-3.5 text-fg-quaternary" />
            </TooltipTrigger>
          </Tooltip>
        </div>
        <Toggle size="md" isSelected={form.offerFreeSamples} onChange={(val) => onChange({ offerFreeSamples: val })} />
      </div>

      {form.offerFreeSamples && (
        <>
          <hr className="border-primary" />
          <div className="flex flex-col gap-6">
            <p className="text-base font-semibold text-primary">Approval rules</p>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => onChange({ approvalRule: "auto-approve" })}
                className="flex flex-1 flex-col items-start gap-2"
              >
                <div
                  className={cx(
                    "w-full rounded-xl border-2 p-12 transition-colors bg-secondary",
                    form.approvalRule === "auto-approve" ? "border-brand" : "border-primary hover:border-secondary",
                  )}
                  style={{
                    background: "url(/collaborations/Autoapprove.png) no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                  }}
                />
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-primary">Unbox Auto Approve requests</span>
                  <span
                    className={cx(
                      "flex size-4 items-center justify-center rounded-full border-2 transition-colors",
                      form.approvalRule === "auto-approve" ? "border-brand" : "border-tertiary",
                    )}
                  >
                    {form.approvalRule === "auto-approve" && <span className="size-2 rounded-full bg-brand-solid" />}
                  </span>
                </div>
                <p className="text-sm text-tertiary text-left">Follows the sample rules that are setup and enabled.</p>
              </button>
              <button
                type="button"
                onClick={() => onChange({ approvalRule: "manually-review" })}
                className="flex flex-1 flex-col items-start gap-2"
              >
                <div
                  className={cx(
                    "w-full rounded-xl border-2 p-12 transition-colors bg-secondary",
                    form.approvalRule === "manually-review" ? "border-brand" : "border-primary hover:border-secondary",
                  )}
                  style={{
                    background: "url(/collaborations/Manual.png) no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                  }}
                />
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-primary">Manually review</span>
                  <span
                    className={cx(
                      "flex size-4 items-center justify-center rounded-full border-2 transition-colors",
                      form.approvalRule === "manually-review" ? "border-brand" : "border-tertiary",
                    )}
                  >
                    {form.approvalRule === "manually-review" && <span className="size-2 rounded-full bg-brand-solid" />}
                  </span>
                </div>
                <p className="text-sm text-tertiary text-left">
                  This collaboration will be excluded from your Unbox Auto Approval Rules, every sample request will be
                  reviewed individually.
                </p>
              </button>
            </div>

            {form.approvalRule === "auto-approve" && (
              <div className="flex items-center justify-between gap-4 rounded-lg bg-unbox-cream-50 px-4 py-3">
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm font-semibold text-primary">
                    {rulesCount === 0 ? "No Rules Active" : `${rulesCount} Rule${rulesCount === 1 ? "" : "s"} Active`}
                  </p>
                  {rulesCount > 0 && (
                    <p className="text-sm text-tertiary">Applied to every collab in this workspace.</p>
                  )}
                </div>
                <Button color="secondary" iconLeading={Ruler} onClick={() => setRulesDrawerOpen(true)}>
                  {rulesCount === 0 ? (
                    "Create Rules"
                  ) : (
                    <>
                      Configure Rules <span className="text-brand-secondary">({rulesCount})</span>
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      <SampleRulesDrawer open={rulesDrawerOpen} onOpenChange={setRulesDrawerOpen} />
    </div>
  );
}

// ============================================================================
// Step 4: Invitation
// ============================================================================

function InvitationStep({
  form,
  onChange,
  errors,
}: {
  form: FormState;
  onChange: (patch: Partial<FormState>) => void;
  errors: Record<string, string>;
}) {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex gap-8">
        <div className="w-48 shrink-0 flex gap-2">
          <MessageTextSquare02 className="size-5 shrink-0 translate-y-[2px] text-tertiary" />
          <div>
            <p className="text-sm font-semibold text-primary">Invitation Message</p>
            <p className="text-sm text-tertiary">
              Introduce yourself and tell creators why you'd love to work with them
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-3 mt-1 mr-1">
          <MessageComposer
            value={form.invitationMessage}
            onChange={(v) => onChange({ invitationMessage: v })}
            variableContext="collab-invitation"
            maxLength={MAX_INVITATION_MESSAGE_LENGTH}
            minHeight={160}
            aria-label="Invitation message"
          />
        </div>
      </div>

      <hr className="border-primary" />

      <div className="flex gap-8">
        <div className="w-48 shrink-0 flex gap-2">
          <Mail02 className="size-5 shrink-0 translate-y-[2px] text-tertiary" />
          <div>
            <p className="text-sm font-semibold text-primary">Contact Info</p>
            <p className="text-sm text-tertiary">How creators can reach you</p>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-4">
          <Input
            label="Email Address"
            isRequired
            type="email"
            value={form.sellerEmail}
            onChange={(v) => onChange({ sellerEmail: v })}
            isInvalid={!!errors.sellerEmail}
            hint={errors.sellerEmail}
            placeholder="you@company.com"
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Validation
// ============================================================================

function validateStep(step: StepIndex, form: FormState): Record<string, string> {
  const errors: Record<string, string> = {};

  if (step === 0) {
    if (!form.name.trim()) errors.name = "Collaboration name is required";
    if (!form.activeUntil) errors.activeUntil = "End date is required";
  }

  if (step === 1) {
    if (form.products.length === 0) errors.products = "At least one product is required";
    for (const product of form.products) {
      if (!product.commissionRate || Number(product.commissionRate) <= 0) {
        errors[`product_${product.id}`] = "Commission rate is required";
      }
    }
  }

  if (step === 3) {
    if (!form.sellerEmail.trim()) errors.sellerEmail = "Email address is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.sellerEmail)) {
      errors.sellerEmail = "Please enter a valid email address";
    }
  }

  return errors;
}

// ============================================================================
// Modal
// ============================================================================

export function CreateTargetCollaborationModal({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void;
}) {
  const navigate = useNavigate();
  const merchant = useMerchant();
  const currency = useCurrency();
  const triggerTypes = useCollabTriggerTypes();
  const [currentStep, setCurrentStep] = useState<StepIndex>(0);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const createMutation = useCreateTargetCollaboration();

  const handleChange = useCallback((patch: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(patch)) {
        delete next[key];
      }
      return next;
    });
  }, []);

  const handleNext = useCallback(() => {
    const stepErrors = validateStep(currentStep, form);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    setCurrentStep((prev) => Math.min(prev + 1, LAST_STEP_INDEX) as StepIndex);
  }, [currentStep, form]);

  const handlePrevious = useCallback(() => {
    setErrors({});
    setCurrentStep((prev) => Math.max(prev - 1, 0) as StepIndex);
  }, []);

  const handleCreate = useCallback(async () => {
    const stepErrors = validateStep(3, form);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    try {
      const result = await createMutation.mutateAsync({
        name: form.name.trim(),
        endTime: new Date(form.activeUntil).toISOString(),
        products: form.products.map((p) => ({
          id: p.id,
          targetCommissionRate: Number(p.commissionRate),
          ...(p.shopAdsEnabled && p.shopAdsCommission !== ""
            ? { shopAdsCommissionRate: Number(p.shopAdsCommission) }
            : {}),
        })),
        sellerEmail: form.sellerEmail.trim(),
        ...(form.invitationMessage.trim() ? { message: form.invitationMessage.trim() } : {}),
        freeSampleRule: {
          hasFreeSample: form.offerFreeSamples,
          isSampleApprovalExempt: form.offerFreeSamples && form.approvalRule === "auto-approve",
        },
        automations: Object.fromEntries(
          triggerTypes.map((triggerType) => [triggerType, form.automations[triggerType]]),
        ),
      });

      onCreated?.();
      onOpenChange(false);
      setForm(INITIAL_FORM);
      setCurrentStep(0);
      setErrors({});

      if (result.collaborationId) {
        navigate({
          to: "/merchant/$merchantId/collaborations/target/$collaborationId",
          params: { merchantId: merchant, collaborationId: result.collaborationId },
        });
      }
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Failed to create collaboration");
    }
  }, [form, createMutation, onCreated, onOpenChange, navigate, merchant, triggerTypes]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setForm(INITIAL_FORM);
        setCurrentStep(0);
        setErrors({});
      }
      onOpenChange(open);
    },
    [onOpenChange],
  );

  const steps = useMemo(() => buildSteps(currentStep, STEP_LABELS), [currentStep]);
  const isLastStep = currentStep === LAST_STEP_INDEX;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <ScrollableDialogContent className="max-w-2xl">
        <DialogTitle className="text-lg font-semibold">New Target Collaboration</DialogTitle>

        <div className="mt-4 mb-6">
          <Progress.IconsWithText type="number" orientation="horizontal" size="sm" items={steps} />
        </div>

        <div className="min-h-[300px] overflow-hidden">
          {currentStep === 0 && <DetailsStep form={form} onChange={handleChange} errors={errors} />}
          {currentStep === 1 && (
            <ProductsStep form={form} onChange={handleChange} errors={errors} currency={currency} />
          )}
          {currentStep === 2 && <SamplesStep form={form} onChange={handleChange} />}
          {currentStep === 3 && <InvitationStep form={form} onChange={handleChange} errors={errors} />}
          {currentStep === 4 && (
            <AutomationsStep
              value={form.automations}
              onChange={(automations) => handleChange({ automations })}
              collaborationName={form.name || "this collaboration"}
            />
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <Button color="secondary" size="md" onClick={handlePrevious} disabled={currentStep === 0}>
            Previous
          </Button>

          {isLastStep ? (
            <Button color="primary" size="md" onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? "Creating..." : "Create Draft"}
            </Button>
          ) : (
            <Button color="primary" size="md" onClick={handleNext}>
              Next
            </Button>
          )}
        </div>
      </ScrollableDialogContent>
    </Dialog>
  );
}
